import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { t } from '@/i18n';
import type { DashboardMonthlyCost } from '@ip/shared-types';
import { ChartCard } from './ChartCard';
import { MONTH_LABELS, formatAxis, monthKey, plnTooltipFormatter } from './chart-utils';

interface MonthlyCostsChartProps {
  data: DashboardMonthlyCost[];
  year: number;
}

export function MonthlyCostsChart({ data, year }: MonthlyCostsChartProps) {
  const chartData = MONTH_LABELS.map((label, i) => {
    const found = data.find((d) => d.month === monthKey(year, i));
    return {
      month: label,
      gross: found?.totalGross ?? 0,
      vat: found?.totalTax ?? 0,
    };
  });

  const config: ChartConfig = {
    gross: { label: t('dashboard.grossAmount'), color: 'var(--chart-1)' },
    vat: { label: t('dashboard.vatAmount'), color: 'var(--chart-3)' },
  };

  return (
    <ChartCard title={t('dashboard.monthlyCosts')}>
      <ChartContainer config={config} className="h-[300px] w-full">
        <BarChart data={chartData}>
          <CartesianGrid vertical={false} strokeOpacity={0.4} />
          <XAxis dataKey="month" tickLine={false} axisLine={false} />
          <YAxis tickFormatter={formatAxis} tickLine={false} axisLine={false} width={64} />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(label) => `${label} ${year}`}
                formatter={plnTooltipFormatter(config)}
              />
            }
          />
          <ChartLegend content={<ChartLegendContent />} />
          <Bar dataKey="gross" fill="var(--color-gross)" radius={[4, 4, 0, 0]} maxBarSize={28} />
          <Bar dataKey="vat" fill="var(--color-vat)" radius={[4, 4, 0, 0]} maxBarSize={28} />
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}
